import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle, CreditCard, Calendar, Users, X, Loader, Upload, BellOff, Ticket } from 'lucide-react';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import useAuthStore from '../store/authStore';

const TYPE_CONFIG = {
  student_created: { icon: Users, color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.1)' },
  students_imported: { icon: Upload, color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
  payment_recorded: { icon: CreditCard, color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
  meeting_scheduled: { icon: Calendar, color: '#6366f1', bg: 'rgba(99, 102, 241, 0.1)' },
  // Package ending (backend sportJobs.runCreditAlerts): amber, red once used up.
  sport_credit_alert: { icon: Ticket, color: 'var(--color-warning-dark)', bg: 'var(--color-warning-light)' },
  sport_credit_alert_used_up: { icon: Ticket, color: 'var(--color-danger-dark)', bg: 'var(--color-danger-light)' },
  default: { icon: Bell, color: 'var(--color-text-tertiary, #64748b)', bg: 'rgba(100, 116, 139, 0.1)' },
};

function formatRelativeTime(timestamp, t) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('time.justNow');
  if (diffMins < 60) return t('time.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
  if (diffDays < 7) return t('time.daysAgo', { count: diffDays });
  return date.toLocaleDateString();
}

export default function NotificationPanel({
  isOpen,
  onClose,
  notifications = [],
  unreadCount = 0,
  isLoading = false,
  onMarkRead,
  onMarkAllRead,
  isMobile = false,
}) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const activeCenterId = useAuthStore((s) => s.centerId);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Delay to avoid immediate close from bell click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('notifications.title', 'Notifications')}
      style={{
        position: 'fixed',
        top: isMobile ? '56px' : '64px',
        right: isRTL ? 'auto' : (isMobile ? '0.5rem' : '1.5rem'),
        left: isRTL ? (isMobile ? '0.5rem' : '1.5rem') : 'auto',
        width: isMobile ? 'calc(100% - 1rem)' : '380px',
        maxHeight: '500px',
        backgroundColor: 'var(--color-surface, #fff)',
        border: '1px solid var(--color-border, #e2e8f0)',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        direction: isRTL ? 'rtl' : 'ltr',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '0.875rem 1rem',
        borderBottom: '1px solid var(--color-border, #e2e8f0)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.9375rem', fontWeight: '600', color: 'var(--color-text-primary, #1e293b)' }}>
            {t('notifications.title', 'Notifications')}
          </span>
          {unreadCount > 0 && (
            <span style={{
              padding: '0.125rem 0.5rem', borderRadius: '9999px',
              backgroundColor: 'var(--color-info-light, #eff6ff)', color: 'var(--color-info-dark, #3b82f6)',
              fontSize: '0.75rem', fontWeight: '600',
            }}>
              {unreadCount} {t('notifications.unread', 'unread')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              style={{
                border: 'none', background: 'none', cursor: 'pointer',
                fontSize: '0.75rem', color: 'var(--color-primary-dark, #355760)',
                fontWeight: '500', padding: '0.25rem 0.5rem', borderRadius: '6px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background, #f1f5f9)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {t('notifications.markAllRead', 'Mark all read')}
            </button>
          )}
          <button
            onClick={onClose}
            aria-label={t('common.closeNotifications')}
            style={{
              border: 'none', background: 'none', cursor: 'pointer',
              padding: '0.25rem', display: 'flex', borderRadius: '4px',
              color: 'var(--color-text-secondary)',
            }}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div style={{ flex: 1, overflowY: 'auto', maxHeight: '400px' }}>
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <Loader size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary-dark)' }} />
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
            <BellOff size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
            <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
              {t('notifications.noNotifications', 'No notifications yet')}
            </div>
            <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
              {t('notifications.noNotificationsHint', 'New activity will appear here')}
            </div>
          </div>
        ) : (
          notifications.map((notif) => {
            const meta = notif.metadata || {};
            const isCredit = notif.type === 'sport_credit_alert';
            const config = (isCredit && meta.kind === 'used_up' && TYPE_CONFIG.sport_credit_alert_used_up)
              || TYPE_CONFIG[notif.type] || TYPE_CONFIG.default;
            const IconComponent = config.icon;
            const isUnread = !notif.isRead;
            // A package alert opens the client — only inside its own center.
            const target = isCredit && meta.studentId && (!meta.centerId || meta.centerId === activeCenterId)
              ? `/students/${meta.studentId}?tab=billing` : null;
            const text = isCredit && meta.kind
              ? t(`notifications.sportCredit.${meta.kind}`, { defaultValue: notif.message || '', date: meta.validUntil || '' })
              : (notif.message || '');
            const onOpen = () => {
              if (isUnread) onMarkRead?.(notif.id);
              if (target) { onClose(); navigate(target); }
            };

            return (
              <div
                key={notif.id}
                onClick={onOpen}
                data-testid={`notification-${notif.id}`}
                style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid var(--color-border, #f1f5f9)',
                  cursor: isUnread || target ? 'pointer' : 'default',
                  transition: 'background-color 0.15s',
                  backgroundColor: isUnread ? 'rgba(59, 130, 246, 0.04)' : 'transparent',
                  borderLeft: !isRTL && isUnread ? '3px solid #3b82f6' : '3px solid transparent',
                  borderRight: isRTL && isUnread ? '3px solid #3b82f6' : '3px solid transparent',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-background, #f8fafc)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isUnread ? 'rgba(59, 130, 246, 0.04)' : 'transparent'; }}
              >
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    backgroundColor: config.bg, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <IconComponent size={16} color={config.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.8125rem',
                      fontWeight: isUnread ? '600' : '400',
                      color: 'var(--color-text-primary, #1e293b)',
                      marginBottom: '0.125rem',
                    }}>
                      {notif.title || '--'}
                    </div>
                    <div style={{
                      fontSize: '0.75rem', color: 'var(--color-text-secondary, #64748b)',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    }}>
                      {text}
                    </div>
                    <div style={{
                      fontSize: '0.6875rem', color: 'var(--color-text-secondary, #94a3b8)',
                      marginTop: '0.25rem',
                    }}>
                      {formatRelativeTime(notif.createdAt, t)}
                    </div>
                  </div>
                  {isUnread && (
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      backgroundColor: '#3b82f6', flexShrink: 0, marginTop: '0.5rem',
                    }} />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
